import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { borrowerActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ValidationMessagesModal, StateSelect, ZipCodeInput, CountySelect } from '../../common/SharedComponents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import DropDownBox from 'devextreme-react/drop-down-box';
import DataGrid, { Selection, Scrolling, Paging, Column } from 'devextreme-react/data-grid';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';

const EditCompanyModal = () => {
    const { isOpen, companyId, onChange } = useSelector((state) => state.borrower.editCompanyModal);
    const { dispatch } = store;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [isUserListOpened, set_isUserListOpened] = useState(false);
    const [noticeUsers, set_noticeUsers] = useState([]);
    const [name, set_name] = useState('');
    const [webSite, set_webSite] = useState('');
    const [street1, set_street1] = useState('');
    const [street2, set_street2] = useState('');
    const [city, set_city] = useState('');
    const [state, set_state] = useState('');
    const [zipCode, set_zipcode] = useState('');
    const [county, set_county] = useState('');
    const [noticesUserId, set_noticesUserId] = useState([]);
    const [physicalAddressSameAsMailing, set_physicalAddressSameAsMailing] = useState(false);
    const [physicalAddressStreet1, set_physicalAddressStreet1] = useState('');
    const [physicalAddressStreet2, set_physicalAddressStreet2] = useState('');
    const [physicalAddressCity, set_physicalAddressCity] = useState('');
    const [physicalAddressState, set_physicalAddressState] = useState('');
    const [physicalAddressZipCode, set_physicalAddressZipCode] = useState('');
    const [physicalAddressCounty, set_physicalAddressCounty] = useState('');

    //Dialogs
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [validation, set_validation] = useState(null);

    const handleClose = () =>
        dispatch(
            borrowerActions.setEditCompanyModal({
                editCompanyModal: { isOpen: false },
            })
        );

    const styles = {
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        buttonMarginRight: { marginRight: '10px' },
        sectionHeader: { margin: '10px 0' },
        sectionSubHeader: { margin: '15px' },
        formColumn: { paddingBottom: '5px', paddingRight: '5px' },
    };

    async function loadCompanyData(id) {
        if (!id) {
            setState(null);
            set_loadingOpen(false);
            return;
        }

        let response = await borrowerApi.getNoticeRecipientList(id);
        if (response.statusCode !== 200) return;
        set_noticeUsers(response.result);

        response = await borrowerApi.getCompanyById(id);
        if (response.statusCode !== 200) return;
        const company = response.result;
        setState(company);

        set_loadingOpen(false);
    }

    useEffect(() => {
        loadCompanyData(companyId);
    }, [companyId]);

    const validateCompany = async () => {
        set_loadingMessage('Validating...');
        try {
            const model = {
                companyId,
                name,
                state,
            };

            const response = await borrowerApi.validateCompany(model);
            if (response.statusCode !== 200 || !response.result) {
                set_loadingOpen(false);
                return false;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            return true;
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
            return false;
        }
    };

    const validateAndSave = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        var isOk = await validateCompany();
        if (!isOk) {
            return;
        }

        await saveCompany();
    };

    const saveCompany = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                companyId,
                webSite,
                name,
                street1,
                street2,
                city,
                state,
                zipCode,
                county,
                noticesUserId: noticesUserId.length > 0 ? noticesUserId[0] : null,
                physicalAddressSameAsMailing,
                physicalAddressStreet1,
                physicalAddressStreet2,
                physicalAddressCity,
                physicalAddressState,
                physicalAddressZipCode,
                physicalAddressCounty,
            };

            set_loadingMessage('Saving changes...');
            const response = await borrowerApi.saveCompany(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            refreshEvents.dispatchRefreshEvent();

            const savedCompany = response.result;
            setState(savedCompany);
            onChange && onChange(savedCompany);

            handleAdminSuccess('Borrower Company Saved successfully.');
            set_loadingOpen(false);

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onValidationConfirm = async () => {
        set_validation(null);
        await saveCompany();
    };

    const onValidationCancel = () => {
        set_validation(null);
    };

    const resetState = () => {
        set_noticeUsers([]);
        set_name('');
        set_webSite('');
        set_street1('');
        set_street2('');
        set_city('');
        set_state('');
        set_zipcode('');
        set_county('');
        set_noticesUserId([]);
        set_physicalAddressSameAsMailing(false);
        set_physicalAddressStreet1('');
        set_physicalAddressStreet2('');
        set_physicalAddressCity('');
        set_physicalAddressState('');
        set_physicalAddressZipCode('');
        set_physicalAddressCounty('');
    };

    const setState = (company) => {
        if (company) {
            set_name(company.name);
            set_webSite(company.webSite);
            set_street1(company.street1);
            set_street2(company.street2);
            set_city(company.city);
            set_state(company.state);
            set_zipcode(company.zipCode);
            set_county(company.county);
            set_noticesUserId(company.noticesUserId ? [company.noticesUserId] : []);
            set_physicalAddressSameAsMailing(company.physicalAddressSameAsMailing);
            set_physicalAddressStreet1(company.physicalAddressStreet1);
            set_physicalAddressStreet2(company.physicalAddressStreet2);
            set_physicalAddressCity(company.physicalAddressCity);
            set_physicalAddressState(company.physicalAddressState);
            set_physicalAddressZipCode(company.physicalAddressZipCode);
            set_physicalAddressCounty(company.physicalAddressCounty);
        } else {
            resetState();
        }
    };

    const onNoticeUsersChanged = (e) => {
        set_noticesUserId(e.value);
    };

    const onNoticeUsesGropDownBoxOpened = (e) => {
        if (e.name === 'opened') {
            set_isUserListOpened(e.value);
        }
    };

    const onNoticeUserSelected = (e) => {
        set_noticesUserId(e.selectedRowKeys);
        set_isUserListOpened(false);
    };

    const noticeUserDisplayExpression = (item) => {
        return item && `${item.userName} <${item.email}>`;
    };

    const noticeUsersRender = () => {
        return (
            <DataGrid
                keyExpr='userId'
                dataSource={noticeUsers}
                hoverStateEnabled={true}
                selectedRowKeys={noticesUserId}
                onSelectionChanged={onNoticeUserSelected}
                height='100%'
                showBorders={true}
                showRowLines={false}
                rowAlternationEnabled={false}
            >
                <Column
                    dataField='userName'
                    caption='Name'
                    width={150}
                />
                <Column
                    dataField='email'
                    caption='Email'
                    width={300}
                />
                <Selection mode='single' />
                <Scrolling mode='virtual' />
                <Paging
                    enabled={true}
                    pageSize={10}
                />
            </DataGrid>
        );
    };

    useEffect(() => {
        if (physicalAddressSameAsMailing) {
            set_physicalAddressStreet1(street1);
            set_physicalAddressStreet2(street2);
            set_physicalAddressCity(city);
            set_physicalAddressState(state);
            set_physicalAddressCounty(county);
            set_physicalAddressZipCode(zipCode);
        }
    }, [physicalAddressSameAsMailing, street1, street2, city, state, zipCode, county]);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='lg'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '950px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='editBorrowerForm'
                                    onSubmit={(event) => validateAndSave(event)}
                                    className={'standard-form'}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1
                                            style={{
                                                height: '50px',
                                                marginTop: '15px',
                                            }}
                                        >
                                            {companyId ? 'Edit Business Information' : 'Add Business Information'}
                                        </h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <div className='standard-form'>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Name
                                            </h2>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-10'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='name'>
                                                        Business Name (used on business card or website){' '}
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        id='name'
                                                        value={name}
                                                        selected={name}
                                                        onChange={(e) => set_name(e.target.value)}
                                                        required={true}
                                                        maxLength={100}
                                                    />
                                                </div>
                                            </div>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='webSite'>WebSite URL</label>
                                                    <input
                                                        type='text'
                                                        pattern='(?:https?://)?(?:[\w]+\.)(?:\.?[\w]{2,})+$'
                                                        title='eg http://www.google.com, http://www.google.com or www.google.com'
                                                        maxLength={500}
                                                        id='webSite'
                                                        value={webSite}
                                                        onChange={(e) => set_webSite(e.target.value)}
                                                        required={false}
                                                    />
                                                </div>
                                            </div>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Address
                                            </h2>
                                            <div style={styles.sectionSubHeader}>Mailing Address</div>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='street1'>
                                                        Street Address <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='street1'
                                                        type='text'
                                                        value={street1}
                                                        onChange={(event) => set_street1(event.target.value)}
                                                        required={true}
                                                        maxLength={50}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='street2'>Street Address 2</label>
                                                    <input
                                                        id='street2'
                                                        type='text'
                                                        value={street2}
                                                        onChange={(event) => set_street2(event.target.value)}
                                                        maxLength={50}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='city'>
                                                        City <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='city'
                                                        type='text'
                                                        value={city}
                                                        onChange={(event) => set_city(event.target.value)}
                                                        required={true}
                                                        maxLength={50}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='state'>
                                                        State <span className='req'>*</span>
                                                    </label>
                                                    <StateSelect
                                                        id='state'
                                                        value={state}
                                                        onChange={(event) => set_state(event.target.value)}
                                                        required={true}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='zipCode'>
                                                        Zip Code <span className='req'>*</span>
                                                    </label>
                                                    <ZipCodeInput
                                                        id='zipCode'
                                                        value={zipCode}
                                                        onChange={(event) => set_zipcode(event.target.value)}
                                                        required={true}
                                                        maxLength={10}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='county'>County</label>
                                                    <CountySelect
                                                        id={'county'}
                                                        value={county ?? ''}
                                                        selectedState={state ?? ''}
                                                        onChange={(event) => set_county(event.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.physicalAddressSameAsMailing}
                                                >
                                                    <input
                                                        id='physicalAddressSameAsMailing'
                                                        type='checkbox'
                                                        checked={physicalAddressSameAsMailing}
                                                        onChange={(e) => set_physicalAddressSameAsMailing(e.target.checked)}
                                                    />{' '}
                                                    Physical Address Same As Mailing Address
                                                </div>
                                            </div>
                                            <div style={styles.sectionSubHeader}>Physical Address</div>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressStreet1'>Street Address</label>
                                                    <input
                                                        id='physicalAddressStreet1'
                                                        type='text'
                                                        value={physicalAddressStreet1}
                                                        onChange={(event) => set_physicalAddressStreet1(event.target.value)}
                                                        disabled={physicalAddressSameAsMailing}
                                                        maxLength={50}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressStreet2'>Street Address 2</label>
                                                    <input
                                                        id='physicalAddressStreet2'
                                                        type='text'
                                                        value={physicalAddressStreet2}
                                                        onChange={(event) => set_physicalAddressStreet2(event.target.value)}
                                                        maxLength={50}
                                                        disabled={physicalAddressSameAsMailing}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressCity'>City</label>
                                                    <input
                                                        id='physicalAddressCity'
                                                        type='text'
                                                        value={physicalAddressCity}
                                                        onChange={(event) => set_physicalAddressCity(event.target.value)}
                                                        maxLength={50}
                                                        disabled={physicalAddressSameAsMailing}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressState'>State</label>
                                                    <StateSelect
                                                        id='physicalAddressState'
                                                        value={physicalAddressState}
                                                        onChange={(event) => set_physicalAddressState(event.target.value)}
                                                        disabled={physicalAddressSameAsMailing}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-3'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressZipCode'>Zip Code</label>
                                                    <ZipCodeInput
                                                        id='physicalAddressZipCode'
                                                        value={physicalAddressZipCode}
                                                        onChange={(event) => set_physicalAddressZipCode(event.target.value)}
                                                        maxLength={10}
                                                        disabled={physicalAddressSameAsMailing}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 medium-6 large-6'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='physicalAddressCounty'>County</label>
                                                    <CountySelect
                                                        id={'physicalAddressCounty'}
                                                        value={physicalAddressCounty ?? ''}
                                                        selectedState={physicalAddressState ?? ''}
                                                        onChange={(event) => set_physicalAddressCounty(event.target.value)}
                                                        disabled={physicalAddressSameAsMailing}
                                                    />
                                                </div>
                                            </div>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Notices
                                            </h2>
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-7'
                                                    style={styles.formColumn}
                                                >
                                                    <label htmlFor='noticesUserId'>Send Notices to</label>
                                                    <DropDownBox
                                                        value={noticesUserId}
                                                        opened={isUserListOpened}
                                                        valueExpr='userId'
                                                        displayExpr={noticeUserDisplayExpression}
                                                        placeholder='Select a recipient...'
                                                        dataSource={noticeUsers}
                                                        onValueChanged={onNoticeUsersChanged}
                                                        onOptionChanged={onNoticeUsesGropDownBoxOpened}
                                                        contentRender={noticeUsersRender}
                                                        showClearButton={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            title='Save changes and close'
                                        >
                                            Save
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and close'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ValidationMessagesModal
                open={!!validation}
                validations={validation}
                handleConfirm={onValidationConfirm}
                handleCancel={onValidationCancel}
            />
        </>
    );
};

export default EditCompanyModal;
